/* eslint-disable @typescript-eslint/unbound-method */
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnInit,
} from '@angular/core';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { Title, Meta } from '@angular/platform-browser';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { finalize } from 'rxjs';

import { ApiSuccessfulLogin, ApiError } from 'src/app/interfaces/api';
import { IStrapiDocument, Login } from 'src/app/interfaces/strapi';
import { AuthService } from 'src/app/services/auth.service';
import { UserService } from 'src/app/services/user.service';
import { PageComponent } from 'src/app/shared/components/page/page.component';
import StrapiService from 'src/app/services/strapi.service';
import { NgIf } from '@angular/common';
import { UniService } from '../../../services/uni.service';
import { SocialLoginButtonsComponent } from '../../../shared/components/social-login-buttons/social-login-buttons.component';
import { ErrorBlockComponent } from '../../../shared/components/error-block/error-block.component';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  imports: [
    SocialLoginButtonsComponent,
    FormsModule,
    ReactiveFormsModule,
    NgIf,
    ErrorBlockComponent,
    RouterLink,
  ],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoginComponent extends PageComponent implements OnInit {
  public override pageData: Login = <Login>{};

  public loginForm!: UntypedFormGroup;

  public submitted = false;

  public loginNotFound = false;

  public accountLocked = false;

  public unverified = false;

  public loggingIn = false;

  public otherError = false;

  public otherErrorMessage = '';

  public verificationResent = false;

  private cdr = inject(ChangeDetectorRef);

  public constructor(
    public override readonly titleService: Title,
    public override readonly metaService: Meta,

    public override readonly strapi: StrapiService,
    private readonly formBuilder: UntypedFormBuilder,
    public readonly authService: AuthService,
    public readonly userService: UserService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly uni: UniService
  ) {
    super(titleService, metaService, strapi);
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe((params) => {
      if (params['referrer'])
        this.authService.referrer = String(params['referrer']);
    });

    this.strapi.getLoginPage().subscribe((data: IStrapiDocument<Login>) => {
      this.parsePageData(data.data.attributes);
      super.setMetas();
      this.cdr.markForCheck();
    });

    this.authService.sanctumCSRF().subscribe({
      next: () => {
        setTimeout(() => {
          this.authService.csrfLoaded = true;
        });
      },
    });

    this.loginForm = this.formBuilder.group({
      email: ['', [Validators.email, Validators.required]],
      password: ['', [Validators.required]],
    });
    // we always want to make sure we clear these whenever we land on the login page.
    localStorage.removeItem('unihomesFlow');
    localStorage.removeItem('universityCmsID');
    localStorage.removeItem('accommodationCmsId');
  }

  protected override parsePageData(page: Login): void {
    this.pageData = page;
    super.parsePageData(page);
  }

  public get formControl(): {
    [key: string]: AbstractControl;
  } {
    return this.loginForm.controls;
  }

  public onLogin(): void {
    this.submitted = true;
    this.loginNotFound = false;
    this.accountLocked = false;
    this.unverified = false;
    this.otherError = false;
    this.otherErrorMessage = '';
    this.verificationResent = false;

    if (this.loginForm.invalid) {
      return;
    }

    if (this.authService.csrfLoaded) {
      this.loggingIn = true;
      this.authService
        .emailLogin(
          String(this.loginForm.get('email')?.value),
          String(this.loginForm.get('password')?.value)
        )
        .pipe(
          finalize(() => {
            this.loggingIn = false;
            this.cdr.markForCheck();
          })
        )
        .subscribe({
          next: (user) => {
            setTimeout(() => {
              this.authService.login(<ApiSuccessfulLogin>user);
              const route = this.authService.redirectAfterLogin ?? '/app';
              this.router.navigate([route]).finally(() => false);
            });
          },
          error: (err: ApiError) => {
            if (err.status === 400) {
              this.authService.migratedUserMustReset = true;
              this.router
                .navigate(['/forgotten-password'])
                .finally(() => false);
            } else if (err.status === 403) {
              this.unverified = true;
            } else if (err.status === 401) {
              this.loginNotFound = true;

              if (err.error?.message === 'Account locked') {
                this.accountLocked = true;
              }
            } else {
              this.otherError = true;
              this.otherErrorMessage =
                'There was an error processing your login. Please refresh the page and try again.';
            }
          },
        });
    } else {
      this.otherError = true;
      this.otherErrorMessage =
        'There was an error logging you in. Please refresh the page and try again.';
    }
  }

  public resendVerification() {
    this.userService
      .resendUnauthenticatedVerificationEmail(
        String(this.loginForm.get('email')?.value)
      )
      .subscribe({
        next: () => {
          this.verificationResent = true;
        },
      });
  }
}
